import { useEffect, useState } from 'react'
import { usePageSeo } from '../../lib/seo'
import { getPublishedNewsItems, type NewsItem } from '../../lib/siteContent'

const navigationItems = [
  { href: '/#about', label: '私たちについて' },
  { href: '/#services', label: 'サービス' },
  { href: '/#strengths', label: '強み' },
  { href: '/#company', label: '会社情報' },
]

export default function News() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [newsItems, setNewsItems] = useState<NewsItem[]>([])

  usePageSeo({
    title: 'お知らせ | 合同会社サクラネクサスメディア',
    description:
      '合同会社サクラネクサスメディアのお知らせ一覧です。企業情報、更新情報、公開情報を掲載しています。',
    path: '/news',
  })

  useEffect(() => {
    void getPublishedNewsItems().then(setNewsItems)
  }, [])

  return (
    <div className="bg-[#fcfeff] text-slate-700">
      <header className="sticky top-0 z-50 border-b border-sky-100 bg-white/88 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a href="/" className="flex flex-col">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Corporate Site</p>
              <p className="text-sm font-semibold text-slate-800">合同会社サクラネクサスメディア</p>
            </div>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {navigationItems.map((item) => (
              <a key={item.href} href={item.href} className="text-sm font-medium text-slate-500 transition hover:text-sky-700">
                {item.label}
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <a href="/contact#contact-form" className="brand-button">
              お問い合わせ
            </a>
          </div>

          <button
            type="button"
            onClick={() => setIsMenuOpen((open) => !open)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-sky-100 bg-white/90 text-sky-700 lg:hidden"
            aria-label="メニューを開く"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="border-t border-sky-100 bg-white lg:hidden">
            <nav className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="border-b border-sky-100 py-3 text-sm font-medium text-slate-600 last:border-b-0"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main>
        <section className="section-light border-b border-sky-100 bg-[linear-gradient(180deg,#ffffff_0%,#f6fbff_100%)]">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <p className="section-eyebrow">News</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-800 sm:text-5xl">お知らせ</h1>
            <p className="mt-6 max-w-3xl text-base leading-8 text-slate-500">
              合同会社サクラネクサスメディアからのお知らせを掲載しています。
            </p>
          </div>
        </section>

        <section className="section-light bg-white">
          <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <div className="space-y-4">
              {newsItems.map((item) => (
                <a
                  key={item.id}
                  href={`/news/${item.slug}`}
                  className="block rounded-[1.75rem] border border-sky-100 bg-white px-6 py-6 shadow-[0_20px_50px_rgba(125,170,230,0.10)] transition hover:border-sky-200"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <span className="text-sm font-semibold text-slate-400">{item.publishedAt.replaceAll('-', '.')}</span>
                        <span className="inline-flex w-fit rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-600">
                          {item.category}
                        </span>
                      </div>
                      <h2 className="mt-4 text-2xl font-semibold text-slate-800">{item.title}</h2>
                      <p className="mt-3 text-sm leading-7 text-slate-500">{item.summary}</p>
                    </div>
                    <span className="text-sm font-medium text-slate-400">詳細を見る</span>
                  </div>
                </a>
              ))}
              {newsItems.length === 0 && (
                <div className="rounded-[1.75rem] border border-sky-100 bg-white px-6 py-8 text-center text-sm text-slate-400">
                  現在ご案内できるお知らせはありません。
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
